import {
  afterBuildTicksFunc,
  colorFunc,
  sizeFunc,
  tooltipLabelCallback,
  tooltipTitleCallback,
  weightFunc
} from '@/utils/chart_helpers';
import ChartStyles from '@/utils/chart_styles';
import Utilities from '@/utils/utilities';
import { Controller } from '@hotwired/stimulus';
import Chart from 'chart.js/auto';
import 'chartjs-adapter-luxon';
import { DateTime } from 'luxon';
import numeral from 'numeral';

export default class extends Controller {
  static targets = ['chart'];
  static values = { taskId: String }

  connect() {
    this.timezoneOffset = 0;
    this.buildChart();
    
    // If a task ID is present, open the task modal directly
    if (this.hasTaskIdValue) {
      setTimeout(() => {
        this.openTaskModal()
      }, 1000) // Longer delay for overview page since it doesn't have tasks section
    }
  }

  disconnect() {
    this.chart.destroy();
  }

  async openTaskModal() {
    try {
      // For the overview page, we need to make a direct request to load the task modal
      // since there's no tasks section to click on
      const taskUrl = `/tasks/client-tasks/${this.taskIdValue}`
      
      // Create a temporary turbo frame to load the task modal
      const frame = document.createElement('turbo-frame')
      frame.id = 'updated-modal-content'
      frame.src = taskUrl
      
      // Find the existing modal content frame and replace it
      const existingFrame = document.getElementById('updated-modal-content')
      if (existingFrame && existingFrame.parentNode) {
        existingFrame.parentNode.replaceChild(frame, existingFrame)
      }
    } catch (error) {
      console.error('Error opening task modal:', error)
    }
  }

  changeTimeFrame(event) {
    const value = event.target.value;
    const preferenceType = 'financial_metrics';
    const preferenceKey = 'time_frame';

    this.savePreference(preferenceType, preferenceKey, value).then(() => {
      const url = new URL(window.location);
      url.searchParams.set('tf', value);

      if (url.searchParams.get('i') === 'year' && !['last_2_years', 'last_3_years'].includes(value)) {
        url.searchParams.delete('i');
      }

      Turbo.visit(url, { action: 'replace' });
    }).catch((error) => {
      console.error('Navigation blocked due to error:', error);
    });
  }

  changeInterval(event) {
    const value = event.target.value;
    const preferenceType = 'financial_metrics';
    const preferenceKey = 'interval';

    this.savePreference(preferenceType, preferenceKey, value).then(() => {
      const url = new URL(window.location);
      url.searchParams.set('i', value);

      Turbo.visit(url, { action: 'replace' });
    }).catch((error) => {
      console.error('Navigation blocked due to error:', error);
    });
  }

  // Save the user preference via an API call to the backend
  savePreference(preferenceType, preferenceKey, value) {
    const csrfToken = document.querySelector('meta[name="csrf-token"]').getAttribute('content');

    return fetch('/user_preferences', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': csrfToken
      },
      body: JSON.stringify({
        user_preference: {
          preference_type: preferenceType,
          preference_key: preferenceKey,
          preference_value: { value: value }
        }
      })
    })
    .then(response => response.json())
    .then(data => {
      if (!data.success) {
        console.error('Failed to save preference:', data.errors);
      }
    })
    .catch(error => {
      console.error('Error saving preference:', error);
    });
  }

  buildOptions(label, color) {
    return {
      backgroundColor: Utilities.opacitize(color, 0.25),
      borderColor: color,
      fill: '+1',
      pointBackgroundColor: color,
      pointHoverBackgroundColor: color,
      label
    };
  }

  buildData(data) {
    return {
      data: data.map(({ x, y }) => ({
        x: DateTime.fromISO(x).setZone(this.timezoneOffset),
        y: y
      }))
    }
  }

  buildChart() {
    const ctx = this.chartTarget;
    const { interval } = ctx.dataset;

    const defaultOptions = {
      pointBorderWidth: 0,
      pointHitRadius: 20,
      pointHoverBorderWidth: 0,
      pointHoverRadius: 8,
      pointRadius: 4
    };

    const plotData = {
      datasets: [
        {
          ...defaultOptions,
          ...this.buildData(JSON.parse(ctx.dataset.revenue)),
          ...this.buildOptions('Revenue', ChartStyles.colors.green)
        },
        {
          ...defaultOptions,
          ...this.buildData(JSON.parse(ctx.dataset.grossProfit)),
          ...this.buildOptions('Gross Profit', ChartStyles.colors.blueGray)
        },
        {
          ...defaultOptions,
          ...this.buildData(JSON.parse(ctx.dataset.operatingIncome)),
          ...this.buildOptions('Operating Income', ChartStyles.colors.darkBlue),
          borderDash: [10, 5]
        },
        {
          ...defaultOptions,
          ...this.buildData(JSON.parse(ctx.dataset.netIncome)),
          ...this.buildOptions('Net Income', ChartStyles.colors.blue),
          fill: 'origin'
        }
      ]
    };

    return this.chart = new Chart(ctx, {
      type: 'line',
      data: plotData,
      options: {
        tension: ChartStyles.tension,
        hover: {
          mode: 'index',
          intersect: false,
          onHover: (_event, chartElements) => {
            const table = document.getElementById('overview-table');
            if (chartElements[0] && table) {
              let tableCtrl = table['table'];
              return tableCtrl.highlightColumnByIndex(chartElements[0]._index + 1);
            }
          }
        },
        animation: {
          duration: 300
        },
        layout: {
          padding: {
            left: 10
          }
        },
        maintainAspectRatio: false,
        scales: {
          x: {
            distribution: 'series',
            type: 'time',
            time: {
              displayFormats: {
                month: 'MMM',
                quarter: 'Qq'
              },
              unit: interval,
              round: interval,
            },
            adapters: {
              date: {
                zone: this.timezoneOffset
              }
            },
            grid: {
              drawOnChartArea: false,
              drawBorder: true
            },
            ticks: {
              major: {
                enabled: true,
              },
              color: colorFunc,
              font: {
                weight: weightFunc,
                size: sizeFunc
              },
              maxRotation: 0,
              maxTicksLimit: 12,
            },
            afterBuildTicks: afterBuildTicksFunc
          },
          y: {
            border: {
              display: false
            },
            grid: {
              display: false
            },
            ticks: {
              color: ChartStyles.axes.ticks.color,
              font: {
                size: ChartStyles.axes.ticks.size
              },
              callback: (value) => (numeral(value).format('$0,0[.]00a'))
            }
          }
        },
        plugins: {
          annotation: {
            annotations: [
              {
                drawTime: 'beforeDatasetsDraw',
                type: 'line',
                mode: 'horizontal',
                scaleID: 'y',
                value: -1,
                borderColor: ChartStyles.colors.blueGrayLight,
                borderWidth: 1,
                borderDash: [10]
              }
            ]
          },
          crosshair: {
            snap: {
              enabled: true
            },
            line: {
              color: ChartStyles.colors.blueGrayLight,
              width: 1,
              dashPattern: [4],
              drawUnderChart: true
            },
            zoom: {
              enabled: false
            }
          },
          legend: {
            position: 'bottom',
            labels: {
              boxWidth: 10,
              padding: 20,
              usePointStyle: true
            }
          },
          tooltip: {
            axis: 'x',
            backgroundColor: ChartStyles.tooltips.backgroundColor,
            bodyColor: ChartStyles.tooltips.bodyFontColor,
            bodySpacing: ChartStyles.tooltips.bodySpacing,
            intersect: false,
            mode: 'index',
            position: 'nearest',
            borderColor: 'rgba(0,0,0,0)',
            titleColor: ChartStyles.tooltips.titleFontColor,
            titleMarginBottom: ChartStyles.tooltips.titleMarginBottom,
            padding: {
              x: ChartStyles.tooltips.padding,
              y: ChartStyles.tooltips.padding,
            },
            callbacks: {
              title: tooltipTitleCallback,
              label: tooltipLabelCallback
            }
          }
        }
      }
    });
  }
}
